import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gift, Copy, Users, CheckCircle, Clock, Send, Loader2, Share2, Ticket, Trophy, Star } from "lucide-react";
import { API, authFetch } from "@/App";

export const ReferralSection = () => {
  const [referralCode, setReferralCode] = useState("");
  const [referralLink, setReferralLink] = useState("");
  const [stats, setStats] = useState({ total: 0, successful: 0 });
  const [myReferrals, setMyReferrals] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]);
  const [showReferDialog, setShowReferDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contestData, setContestData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [referForm, setReferForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    fetchReferralCode();
    fetchMyReferrals();
    fetchMyCoupons();
    fetchContestData();
  }, []);

  const fetchContestData = async () => {
    try {
      const [contestRes, leaderboardRes] = await Promise.all([
        authFetch(`${API}/referral-contests/current`),
        authFetch(`${API}/referral-contests/leaderboard`)
      ]);
      if (contestRes.ok) {
        const data = await contestRes.json();
        setContestData(data);
      }
      if (leaderboardRes.ok) {
        const data = await leaderboardRes.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (_error) {
      console.error("Error fetching contest data:", _error);
    }
  };

  const fetchReferralCode = async () => {
    try {
      const res = await authFetch(`${API}/referrals/my-code`);
      if (res.ok) {
        const data = await res.json();
        setReferralCode(data.referral_code);
        setReferralLink(data.referral_link);
        setStats({
          total: data.total_referrals,
          successful: data.successful_referrals
        });
      }
    } catch (_error) {
      console.error("Error fetching referral code:", _error);
    }
  };

  const fetchMyReferrals = async () => {
    try {
      const res = await authFetch(`${API}/referrals/my-referrals`);
      if (res.ok) {
        setMyReferrals(await res.json());
      }
    } catch (_error) {
      console.error("Error fetching referrals:", _error);
    }
  };

  const fetchMyCoupons = async () => {
    try {
      const res = await authFetch(`${API}/referrals/my-coupons`);
      if (res.ok) {
        setMyCoupons(await res.json());
      }
    } catch (_error) {
      console.error("Error fetching coupons:", _error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleSubmitReferral = async () => {
    if (!referForm.name || !referForm.email) {
      toast.error("Name and email are required");
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/referrals/submit`, {
        method: "POST",
        body: JSON.stringify(referForm)
      });

      if (res.ok) {
        toast.success("Referral submitted successfully! Your friend will receive $25 off their first session.");
        setShowReferDialog(false);
        setReferForm({ name: "", email: "", phone: "", message: "" });
        fetchMyReferrals();
        fetchReferralCode();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit referral");
      }
    } catch (_error) {
      toast.error("Failed to submit referral");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-700";
      case "signed_up": return "bg-blue-100 text-blue-700";
      case "contacted": return "bg-yellow-100 text-yellow-700";
      case "expired": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="space-y-6">
      {/* Referral Hero Card */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Gift className="w-6 h-6" />
            Refer a Friend, Earn Rewards
          </CardTitle>
          <CardDescription className="text-green-700">
            Share My Biz Consulting with your network. You both get $25 off!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Stats */}
            <div className="flex gap-4">
              <div className="text-center p-4 bg-white rounded-lg border border-green-100 flex-1">
                <p className="text-3xl font-bold text-green-600">{stats.total}</p>
                <p className="text-sm text-gray-600">Total Referrals</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-green-100 flex-1">
                <p className="text-3xl font-bold text-green-600">{stats.successful}</p>
                <p className="text-sm text-gray-600">Successful</p>
              </div>
            </div>

            {/* Referral Code */}
            <div className="space-y-3">
              <div>
                <Label className="text-green-700">Your Referral Code</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={referralCode} 
                    readOnly 
                    className="bg-white font-mono text-lg"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(referralCode)}
                    className="border-green-300 hover:bg-green-50"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(referralLink)}
                  className="flex-1 border-green-300 hover:bg-green-50"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Dialog open={showReferDialog} onOpenChange={setShowReferDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700">
                      <Send className="w-4 h-4 mr-2" />
                      Refer Someone
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Refer a Friend</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700">
                        <Gift className="w-4 h-4 inline mr-2" />
                        Your friend gets <strong>$25 off</strong> their first session. When they sign up, you also get <strong>$25 off</strong>!
                      </div>
                      <div className="space-y-2">
                        <Label>Friend's Name *</Label>
                        <Input
                          value={referForm.name}
                          onChange={(e) => setReferForm({ ...referForm, name: e.target.value })}
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Friend's Email *</Label>
                        <Input
                          type="email"
                          value={referForm.email}
                          onChange={(e) => setReferForm({ ...referForm, email: e.target.value })}
                          placeholder="friend@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone (Optional)</Label>
                        <Input
                          type="tel"
                          value={referForm.phone}
                          onChange={(e) => setReferForm({ ...referForm, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Personal Message (Optional)</Label>
                        <Textarea
                          value={referForm.message}
                          onChange={(e) => setReferForm({ ...referForm, message: e.target.value })}
                          placeholder="Hey! I thought you might be interested in this consulting service..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowReferDialog(false)}>Cancel</Button>
                      <Button 
                        onClick={handleSubmitReferral} 
                        disabled={submitting}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {submitting ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                        ) : (
                          <><Send className="w-4 h-4 mr-2" />Send Referral</>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Coupons */}
      {myCoupons.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-green-600" />
              Your Reward Coupons
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {myCoupons.map((coupon) => (
                <div 
                  key={coupon.code} 
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
                >
                  <div>
                    <p className="font-mono font-bold text-green-700">{coupon.code}</p>
                    <p className="text-sm text-green-600">${coupon.discount_amount} off your next session</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {coupon.is_used ? (
                      <Badge variant="outline" className="bg-gray-100">Used</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(coupon.code)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Contest & Leaderboard */}
      {contestData && (
        <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <Trophy className="w-5 h-5 text-yellow-600" />
              {contestData.contest?.name || "Monthly Referral Contest"}
            </CardTitle>
            <CardDescription>{contestData.contest?.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* My Points */}
            <div className="bg-white p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500">Your Points</p>
                  <p className="text-3xl font-bold text-yellow-700">{contestData.my_stats?.total_points || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-neutral-500">Referrals This Month</p>
                  <p className="text-xl font-semibold">{contestData.my_stats?.referrals_submitted || 0}</p>
                </div>
              </div>
            </div>

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <div>
                <h4 className="font-medium text-neutral-700 mb-2 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Top Referrers
                </h4>
                <div className="space-y-2">
                  {leaderboard.slice(0, 5).map((entry, idx) => (
                    <div 
                      key={entry.user_id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        idx === 0 ? 'bg-yellow-100 border border-yellow-300' :
                        idx === 1 ? 'bg-gray-100 border border-gray-300' :
                        idx === 2 ? 'bg-orange-100 border border-orange-300' :
                        'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-yellow-500 text-white' :
                          idx === 1 ? 'bg-gray-400 text-white' :
                          idx === 2 ? 'bg-orange-400 text-white' :
                          'bg-neutral-200'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="font-medium text-sm">{entry.name}</span>
                      </div>
                      <span className="font-bold text-sm">{entry.total_points} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Points Info */}
            <div className="text-xs text-neutral-500 bg-white p-3 rounded border">
              <p className="font-medium mb-1">How to earn points:</p>
              <ul className="space-y-1">
                <li>• +10 pts for each referral submitted</li>
                <li>• +50 pts when referral converts to client</li>
                <li>• +25 bonus at 3 referrals, +50 at 5 referrals</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Referrals List */}
      {myReferrals.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Your Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {myReferrals.map((ref) => (
                <div 
                  key={ref.referral_id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{ref.referred_name}</p>
                    <p className="text-sm text-gray-500">{ref.referred_email}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge className={getStatusColor(ref.status)}>
                    {ref.status === "approved" && <CheckCircle className="w-3 h-3 mr-1" />}
                    {ref.status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                    {ref.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReferralSection;
